import { useEffect, useRef, useState } from 'react';

/**
 * Minimal data-fetching hook. Re-runs whenever `deps` changes.
 * `fetcher` is called on each dep change; only the result of the most recent
 * call is kept (stale results from concurrent calls are discarded).
 *
 * `initialData`, when given, is used as the first result and the first fetch is
 * skipped; later dep changes fetch as usual.
 */
export function useQuery<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
  initialData: T | null = null,
): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData]       = useState<T | null>(initialData);
  const [loading, setLoading] = useState(initialData == null);
  const [error, setError]     = useState<string | null>(null);
  const versionRef            = useRef(0);
  const seededRef             = useRef(initialData != null);

  useEffect(() => {
    if (seededRef.current) {
      seededRef.current = false;
      return;
    }
    let cancelled = false;
    const version = ++versionRef.current;

    setLoading(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (cancelled || version !== versionRef.current) return;
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled || version !== versionRef.current) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}
